<template>
    <input 
        type="number" 
        :id="id" 
        :value="displayValue" 
        @input="set" 
        @wheel.stop 
        @keydown="filterKeys" 
        @blur="onBlur"
        :min="min" 
        :max="max"
        class="textfield">
</template>
<script>
import { debounce } from 'lodash';

const centimeterPerInch = 2.54;
const meterPerFoot = .3048;

const ALLOWED_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '.', 'Backspace', 'Delete'];

export default {
    props: {
        id: {type:String},
        uid: {type:String},
        value: {type: [String, Number]},
        min: {type:Number},
        max: {type:Number},
        precision:{
            type:Number,
            required:false,
            default:2
        },
        cmToIn: {type:Boolean},
        mToFt: {type:Boolean},
        emitImperial: {
            type:Boolean,
            required:false,
            default:true
        },
    },
    data: () => ({
        currentValue: null,
    }),
    watch: {
        value(val) {
            this.currentValue = this.convert(val); // Expects value to be passed from parent as Imperial standard
        },
    },
    computed: {
        displayValue() {
            return this.format(this.currentValue);
        },
        isMetric() {
            if (this.cmToIn || this.mToFt) {
                return true;
            }
            return false;
        }
    },
    methods: {
        unconvert(v) {
            if (!v) {
                return '';
            }
            v = parseFloat(v);
            if (this.cmToIn) {
                return this.format(v / centimeterPerInch);
            } else if (this.mToFt) {
                return this.format(v / meterPerFoot);
            } else {
                return this.format(v);
            }
        },
        convert(v) {
            if (!v) {
                return '';
            }
            v = parseFloat(v);
            if (this.cmToIn) {
                return this.format(v * centimeterPerInch);
            } else if (this.mToFt) {
                return this.format(v * meterPerFoot);
            } else {
                return this.format(v);
            }
        },
        format(v) {
            if (!v) {
                return '';
            }
            
            return Number(v).toFixed(this.precision);
        },
        filterKeys(evt) {
            if (evt.ctrlKey || evt.altKey || evt.metaKey || ALLOWED_KEYS.includes(evt.key)) {
                return true;
            } else {
                evt.preventDefault();
                return false;
            }
        },
        emitInput(value) {
            if (this.emitImperial) {
                value = this.unconvert(value);  // Expects value to be returned as Imperial standard
            }
            this.$emit('input', value);
        },
        set: debounce(function(evt) {
            this.setCurrentValue();
        }, 1000),
        setCurrentValue() {
            let val = this.getInputValue(); // Handles input max limit
            
            if (this.currentValue != val) {
                this.currentValue = val;

                this.emitInput(this.currentValue);
            }

            // Manually fix edge case where displayValue gets updated but input value doesn't reflect change
            if (this.$el && this.$el.value != this.displayValue) {
                this.$el.value = this.displayValue;
            }
        },
        getInputValue() {
            // Use unconvert() to force the number to imperial, enforce the limits, then use convert() to set it to the correct measurement standard 
            let val = '';
            if (this.$el && this.$el.value) {
                val = this.unconvert(this.$el.value);
            }

            if (val) {
                val = Number(val);

                if (this.min !== undefined && val < this.min) {
                    val = Number(this.min);
                }

                if (this.max !== undefined && val > this.max) {
                    val = Number(this.max);
                }
            }

            return this.convert(val);
        },
        onBlur() {
            this.setCurrentValue();
        }
    },
    mounted() {
        this.currentValue = this.value;
    }
}
</script>

<style>
/* For Chrome, Safari, Edge, Opera */
input.textfield::-webkit-outer-spin-button,
input.textfield::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* For Firefox */
input.textfield {
  -moz-appearance: textfield;
}

</style>